import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '../store';
import { EditPositionSlice, PositionSlice } from './types';
import { getPositions, getPositionTimeperiods, getPositionSamples } from '../../api/positions';
import { updateZoom, updateSelectionZoom } from '../devices/mapSettingsSlice';
import { loadSites } from './sitesSlice';
import { batch } from 'react-redux';
import moment from 'moment-timezone';
import { DateTime } from 'luxon';
import { findBounds } from '../../lib/map';
import { notUndefined } from '../../lib/typeCheck';
// https://daveceddia.com/access-redux-store-outside-react/

const initialState: PositionSlice[] = [];

let nextEventId = 4;

export const positionsSlice = createSlice({
  name: 'positions',
  initialState,
  reducers: {
    loadPositions: (state, action: PayloadAction<PositionSlice[]>) => {
      return action.payload;
    },
    updatePosition: (state) => {
      state[0].status.online_status = 'onine';
    },
    toggleListExpanded: (state, action: PayloadAction<string>) => {
      const position = state.find(position => position.station_id == action.payload );
      position.list_expanded = !position.list_expanded;
    },
    togglePositionStatusBoxDisplay: (state, action: PayloadAction<string>) => {
      const position = state.find(position => position.station_id == action.payload );
      position.status_box_display = !position.status_box_display;
    },
    setPositionFocusedTimeperiod: (state, action: PayloadAction<{ id: string; start: string; end: string; }>) => {
      const position = state.find(position => position.station_id == action.payload.id);
      position.timeperiods.forEach(timeperiod => {
        timeperiod.is_selected = false;
        if (DateTime.fromISO(timeperiod.start).equals(DateTime.fromISO(action.payload.start)) && DateTime.fromISO(timeperiod.end).equals(DateTime.fromISO(action.payload.end))) {
          timeperiod.is_selected = true;
        }
      })
    },
    updatePositionTimeperiods: (state, action: PayloadAction<EditPositionSlice>) => {
      const position = state.find(position => position.station_id == action.payload.id);

      if (notUndefined(action.payload.limit_exceedance_summary)) position.limit_exceedance_summary = action.payload.limit_exceedance_summary;
      if (notUndefined(action.payload.timeperiods)) position.timeperiods = action.payload.timeperiods;
    },
    updatePositionSamples: (state, action: PayloadAction<EditPositionSlice>) => {
      const position = state.find(position => position.station_id == action.payload.id);
      if (notUndefined(action.payload.samples)) position.samples = action.payload.samples;    
    },
    toggleSelected: (state, action: PayloadAction<EditPositionSlice>) => {
      const id = action.payload.id;
      state.forEach((position, index) => {
        state[index].selected = (state[index].id === id) ? true : false;
      });
    }
  }
});

export const { loadPositions, updatePosition, toggleListExpanded, togglePositionStatusBoxDisplay, setPositionFocusedTimeperiod, updatePositionTimeperiods, updatePositionSamples, toggleSelected } = positionsSlice.actions;

export const selectAllPositions = (state: RootState) => {  
  return state.positions
};

export const fetchPositions = (refreshZoom=true): AppThunk => async (dispatch, getState) => {
  const state = getState()
  const user = state.user;  
  const oldPositions = state.positions;
  let sites = [];
    
  let selectedStatus = false;
  // find selected position for the detail bar
  const selectedPosition = oldPositions.filter((oldPosition) => oldPosition.selected === true);
  const selectedPositionTimeperiods = selectedPosition.length > 0? await getPositionTimeperiods(user.api_key, selectedPosition[0].id) : null;
  const selectedPositionSamples = selectedPosition.length > 0? await getPositionSamples(user.api_key, selectedPosition[0].id) : null;
  // console.log(selectedPositionTimeperiods);

  try {
    const positions = await getPositions(user.api_key, user.account.id);       
    const combined_data =  positions.map((position, index) => {
      // Handles the status of the display box
      const oldPosition = oldPositions.filter((oldPosition) => oldPosition.station_id === position.id)[0] || null;
      const site_id = 's-'+position.site_id

      // Prepares a temporary array to keep site details

      // Checks if the same site is being added to the array
      const site_exist = sites.filter(site => site.id === ('s-'+position.site_id));
      if (!site_exist.length){
        let temp_site = {
          id:'s-'+position.site_id,
          title: position.site.title,
          status_box_display: false       
        }
        sites.push(temp_site);         
      }   
      
      if (selectedPosition.length > 0){
        if(position.id === selectedPosition[0].id){
          selectedStatus = position.id === selectedPosition[0].id;
          const newSelectedTimePeriods = selectedPositionTimeperiods === selectedPosition[0].timeperiods? selectedPosition[0].timeperiods : selectedPositionTimeperiods;
          const newSelectedSamples = selectedPositionSamples === selectedPosition[0].samples? selectedPosition[0].timeperiods : selectedPositionSamples;
          return {
            ...position,
            site_id: site_id,
            station_id: position.id,        
            status_box_display: oldPosition === null || !oldPosition.status_box_display? false : true,
            list_expanded: false,
            selected: selectedStatus,
            samples: newSelectedSamples,
            timeperiods: newSelectedTimePeriods
          }
        } else {
          return {
            ...position,
            site_id: site_id,
            station_id: position.id,        
            status_box_display: oldPosition === null || !oldPosition.status_box_display? false : true,
            list_expanded: false,
            selected: selectedStatus,
          }
        }
      } else {
        return {
          ...position,
          site_id: site_id,
          station_id: position.id,        
          status_box_display: oldPosition === null || !oldPosition.status_box_display? false : true,
          list_expanded: false,
          selected: selectedStatus,
        }
      }
    })
    const mapBounds = findBounds(combined_data, 700, 500, 5);
    
    batch(() => {
      dispatch(loadSites(sites));
      dispatch(loadPositions(combined_data));
      if (refreshZoom) {
        dispatch(updateZoom(mapBounds));
        dispatch(updateSelectionZoom(mapBounds));
      }
    })
    
  } catch (err) {
    console.error('Failed to load positions')
  }
};

export const fetchPositionTimePeriods = (position: EditPositionSlice): AppThunk => async (dispatch, getState) => {
  
  // This will retrieve timeperiods for a specified position, 
  // then it will update the state of the position by adding its timeperiods
  // and calculated exceedances limit summary

  const { user } = getState()
  
  try {

    const position_id = position.id;    
    const positionTimeperiods = await getPositionTimeperiods(user.api_key, position_id)
    
    if (positionTimeperiods.length>0){

      // Time periods are return as indexes on object instead of array
      // Filter to separate from primative key-values
      // Assumes timeperiods are objects not primatives
      const position_timeperiod_data_array = Object.values(positionTimeperiods).filter(value => Object.prototype.toString.call(value) == "[object Object]")
        .map((timeperiod: any) => {
          return {
            ...timeperiod,
            is_selected: timeperiod.is_live
          }
        })
      
      const summary = findPreviousTimebaseExceedances(position.timezone, position_timeperiod_data_array, 5)
      if(summary === null){
        // show time periods even if the position isn't live
        dispatch(updatePositionTimeperiods({id: position_id, timeperiods: position_timeperiod_data_array, limit_exceedance_summary: undefined}))
      } else {
        dispatch(updatePositionTimeperiods({id: position_id, timeperiods: position_timeperiod_data_array, limit_exceedance_summary: summary.limit_exceedance_summary}))
      }
    }    
    
  }
  catch (err) {
    console.error('Failed to load time periods')
  }
}

const findPreviousTimebaseExceedances = (timezone, time_periods, count) => {
  
  let now = moment().tz(timezone);
  
  const current_timeperiod = time_periods.filter(time_period => {
    let start =  moment(time_period.start);
    let end = moment(time_period.end);
    if (now > start && now <= end) {
      return true;
    }
  })[0];
  
  if (typeof current_timeperiod === 'undefined') return null;
  if (typeof current_timeperiod.limits[0] === 'undefined') return null;
  if (typeof current_timeperiod.limits[0].timebases === 'undefined') return null;
  
  let current_timebase_index = null;
  let previous_timebase_index = null;
  let current_timebase_colour = 'green';
  let previous_timebases_colour = 'green';
  let limit_exceedance_summary = [];
  
  current_timeperiod.limits.forEach(limit => {
    
    let limit_summary = {
      id: limit.id,
      label: limit.label || null,
      type: limit.type || null,
      timebase: limit.timebase || null,
      channel: typeof limit.channel === 'undefined'? null : limit.channel,
      exceedances: []
    }
    
    limit.timebases.forEach((timebase, index) => {
      let start =  moment(timebase.start);
      let end = moment(timebase.end);
      if (now > start && now <= end) {
        current_timebase_index = index;
      }
      let last_timebase = now.clone().subtract(limit.timebase, 's');
      if (last_timebase > start && last_timebase <= end) {
        previous_timebase_index = index;
      }
      
      let timebase_exccedance = null
      
      timebase.exceedances.reverse().forEach(exceedance => {
        previous_timebases_colour = previous_timebases_colour !== 'red'? exceedance.state : previous_timebases_colour;
        if (timebase_exccedance === null || (timebase_exccedance !== null && timebase_exccedance.state !== 'red')) {
          timebase_exccedance = exceedance;
        }
      })
      
      if (timebase_exccedance !== null) {
        limit_summary.exceedances.unshift(timebase_exccedance);
      }
    })
    
    if (current_timebase_index !== null) {
      limit.timebases[current_timebase_index].exceedances.forEach(exceedance => {
        current_timebase_colour = current_timebase_colour !== 'red'? exceedance.state : current_timebase_colour;
      })
    }
    
    limit_exceedance_summary.push(limit_summary);
    
  })
  
  return {
    // current_timebase: current_timebase_index === null? null : current_timeperiod.limits[0].timebases[current_timebase_index],
    // current_timebase_colour: current_timebase_colour,
    // previous_timebases_colour: previous_timebases_colour,
    // previous_timebases: previous_timebase_index === null? null : current_timeperiod.limits[0].timebases[previous_timebase_index],
    limit_exceedance_summary
  }
}

export const fetchPositionSamples = (position: EditPositionSlice): AppThunk => async (dispatch, getState) => {
  
  const { user } = getState()
  
  try {

    const position_id = position.id;    
    const positionSamples = await getPositionSamples(user.api_key, position_id)
    // console.log(positionSamples.length);
    if (positionSamples.length>0){  
      dispatch(updatePositionSamples({id: position_id, samples: positionSamples}))
    }    
    
  }
  catch (err) {
    console.error('Failed to load samples.')
  }
}

export default positionsSlice.reducer;
